import React, { FC, useRef, useEffect, useState } from 'react';
import { isEmpty, trim } from 'lodash';
import { Form, Input } from 'antd';
import { CHATBOT_INITIALIZE_TEXT } from 'modules/chatbot/constants/constants';
import { ReactComponent as SendIcon } from 'images/chatbot/ai-chat-send-icon.svg';
import { CHATBOT_TEST_IDS } from 'modules/chatbot/constants/testIds';

import styles from './brokerChatInput.module.less';

const { TextArea } = Input;

type FormValues = {
  chatInput: string;
};

type ChatInputProps = {
  onSubmit?: (value: string) => void;
  loading?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
};

const BrokerChatInput: FC<ChatInputProps> = ({
  onSubmit,
  loading,
  maxLength = 4000,
  autoFocus = true,
}: ChatInputProps) => {
  const [form] = Form.useForm<FormValues>();
  const [isComposing, setIsComposing] = useState(false);
  const textAreaRef = useRef<any>(null);

  const handleFinish = ({ chatInput }: FormValues) => {
    if (isEmpty(chatInput) || isComposing) return;
    const trimmedValue = trim(chatInput);
    onSubmit?.(trimmedValue);
    form.resetFields();
    textAreaRef.current?.focus();
  };

  const handlePressEnter = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey && !isComposing) {
      event.preventDefault();
      form.submit();
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  useEffect(() => {
    if (autoFocus && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [autoFocus]);

  const currentValue = form.getFieldValue('chatInput') || '';
  const characterCount = currentValue.length;

  return (
    <div
      className={styles.inputContainer}
      data-testid={CHATBOT_TEST_IDS.INPUT_CONTAINER}
    >
      <Form
        form={form}
        className={styles.chatInputForm}
        onFinish={handleFinish}
      >
        <Form.Item name="chatInput" className={styles.formItem}>
          <TextArea
            ref={textAreaRef}
            className={styles.chatInput}
            placeholder={CHATBOT_INITIALIZE_TEXT}
            onPressEnter={handlePressEnter}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            autoSize={{ maxRows: 4 }}
            disabled={loading}
            maxLength={maxLength}
            showCount={false}
            data-testid={CHATBOT_TEST_IDS.INPUT_FIELD}
            aria-label="Type your message"
            aria-describedby="chat-input-help"
            aria-invalid={characterCount > maxLength}
            role="textbox"
            aria-multiline="true"
          />
        </Form.Item>
        <div
          className={`${styles.sendButton}`}
          onClick={form.submit}
          data-testid={CHATBOT_TEST_IDS.SEND_BUTTON}
        >
          <SendIcon data-testid={CHATBOT_TEST_IDS.SEND_BUTTON_ICON} />
        </div>
      </Form>

      {/* Hidden help text for screen readers */}
      <div id="chat-input-help" className={styles.visuallyHidden}>
        Press Enter to send your message, Shift+Enter for a new line, Escape to
        clear
      </div>
    </div>
  );
};

export default BrokerChatInput;
